import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import debounce from '@utils/debounce';
import { calculateRows } from './components/helper/textarea';
import Underline from './components/Underline';
import Component from './components/Component';
import Label from './components/Label';
import Container from './components/Container';
import Message from './components/Message';

const ONLY_NUMBERS_REGEX = /^[0-9\b]+$/;

const ActionIcon = styled.div`
  position: absolute;
  right: 0;
  top: 15px;
`;

function isValidValue(value) {
  return Boolean(value?.length > 0 || Number(value));
}

function getTypeOverride(type) {
  // this is to prevent '.' and 'e' in numeric inputs
  if (type === 'number') return 'tel';

  return type;
}

function getInputProps({
  isTextArea,
  type,
  value,
  rows,
  required,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  ref,
  autoFocus,
  minLength,
  maxLength,
  pattern,
  placeholder,
  paddingLeft,
  elementId,
  disabled,
  autoCapitalize,
}) {
  const props = {
    as: isTextArea ? 'textarea' : 'input',
    type: getTypeOverride(type),
    value,
    isTextArea,
    required,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    ref,
    autoFocus,
    paddingLeft,
    autoComplete: 'off',
    id: elementId,
    disabled,
  };

  if (isTextArea) props.rows = rows;
  if (minLength) props.minLength = minLength;
  if (maxLength) props.maxLength = maxLength;
  if (pattern) props.pattern = pattern;
  if (placeholder) props.placeholder = placeholder;
  if (autoCapitalize) props.autoCapitalize = autoCapitalize;

  return props;
}

const INPUT_DEBOUNCE = 1241;

export class Input extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showUnderline: false,
      rows: props.minRows,
      showDebounceErrorMessage: props.showErrorMessage || false,
    };

    if (!props.inputRef) this.inputRef = React.createRef();
  }

  componentDidMount() {
    const { isTextArea } = this.props;
    const ref = this.getRef();
    if (isTextArea && ref) {
      this.setState({ rows: calculateRows(ref.current, this.props) });
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { showErrorAlways, showErrorMessage } = nextProps;
    const { showDebounceErrorMessage } = prevState;
    if (showErrorAlways && showErrorMessage && !showDebounceErrorMessage) {
      return { showDebounceErrorMessage: true };
    }
    return null;
  }

  cancelDebounce() {
    this.setErrorMessage();
    this.debounce.cancel();
  }

  setErrorMessage = () => {
    const { showErrorMessage } = this.props;
    this.setState({ showDebounceErrorMessage: showErrorMessage });
  };

  manageDebounce = shouldDebounce => {
    if (shouldDebounce) {
      this.debounce();
      this.setState({ showDebounceErrorMessage: false });
    } else this.cancelDebounce();
  };

  debounce = debounce(this.setErrorMessage, INPUT_DEBOUNCE);

  getRef() {
    const { inputRef } = this.props;
    return inputRef || this.inputRef;
  }

  onFocus = e => {
    const { onFocus } = this.props;
    this.setState({ showUnderline: true });
    if (onFocus) onFocus(e);
  };

  onBlur = e => {
    const { onBlur } = this.props;
    if (onBlur) onBlur(e);
    this.setState({ showUnderline: false });
    this.manageDebounce();
  };

  checkValue = value => {
    const { type } = this.props;

    return !(
      value &&
      (type === 'number' || type === 'tel') &&
      !ONLY_NUMBERS_REGEX.test(value)
    );
  };

  onChange = ({ target }) => {
    const { onChange, isTextArea } = this.props;
    const { value } = target;

    if (!this.checkValue(value)) return;

    onChange(value);

    this.manageDebounce(value);

    if (isTextArea) {
      this.setState({ rows: calculateRows(target, this.props) });
    }
  };

  render() {
    const {
      elementId,
      type,
      value,
      required,
      isTextArea,
      autoFocus,
      minLength,
      maxLength,
      pattern,
      placeholder,
      infoMessage,
      showErrorMessage,
      showSuccessMessage,
      iconToShow,
      label,
      errorMessage,
      successMessage,
      showMessage = true,
      paddingLeft,
      onKeyDown,
      withUnderline,
      disabled,
      autoCapitalize,
    } = this.props;

    const { showUnderline, rows, showDebounceErrorMessage } = this.state;
    const hasValue = isValidValue(value);
    const showPlaceholder = placeholder || hasValue || showUnderline;
    const shouldShowErrorMessage = showErrorMessage && showDebounceErrorMessage;
    const showInfoMessage =
      !shouldShowErrorMessage && !showSuccessMessage && Boolean(infoMessage);

    return (
      <>
        {!!label && (
          <Label
            showUnderline={showUnderline}
            showPlaceholder={showPlaceholder}
            showSuccessMessage={showSuccessMessage}
            showErrorMessage={shouldShowErrorMessage}
            hasValue={hasValue}
            htmlFor={elementId}
          >
            {label}
          </Label>
        )}

        <Component
          {...getInputProps({
            elementId,
            isTextArea,
            type,
            value,
            rows,
            required,
            autoFocus,
            minLength,
            maxLength,
            pattern,
            placeholder,
            paddingLeft,
            onChange: this.onChange,
            onBlur: this.onBlur,
            onFocus: this.onFocus,
            onKeyDown,
            ref: this.getRef(),
            disabled,
            autoCapitalize,
          })}
        />

        {!!iconToShow && <ActionIcon>{iconToShow}</ActionIcon>}
        {withUnderline && (
          <Underline
            showUnderline={showUnderline}
            showSuccessMessage={showSuccessMessage}
            showErrorMessage={shouldShowErrorMessage}
          />
        )}

        {showMessage && (
          <Message
            showErrorMessage={shouldShowErrorMessage}
            showSuccessMessage={showSuccessMessage}
            errorMessage={errorMessage}
            successMessage={successMessage}
            showInfoMessage={showInfoMessage}
            infoMessage={infoMessage}
          />
        )}
      </>
    );
  }
}

Input.propTypes = {
  // Data
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  type: PropTypes.string,
  errorMessage: PropTypes.string,
  infoMessage: PropTypes.string,
  successMessage: PropTypes.string,
  required: PropTypes.bool,
  showErrorMessage: PropTypes.bool,
  showSuccessMessage: PropTypes.bool,
  inputRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  isTextArea: PropTypes.bool,
  elementId: PropTypes.string,
  autoFocus: PropTypes.bool,
  maxRows: PropTypes.number,
  minRows: PropTypes.number,
  minLength: PropTypes.string,
  maxLength: PropTypes.string,
  pattern: PropTypes.string,
  paddingLeft: PropTypes.string,
  textareaLineHeight: PropTypes.number,
  iconToShow: PropTypes.node,
  showErrorAlways: PropTypes.bool, // Force to show error message despite of debounce.
  placeholder: PropTypes.string,
  showMessage: PropTypes.bool,
  className: PropTypes.string,
  withUnderline: PropTypes.bool,
  disabled: PropTypes.bool,
  autoCapitalize: PropTypes.oneOf(['characters', 'none', 'sentences', 'words']),

  // Callbacks
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
};

Input.defaultProps = {
  required: false,
  value: '',
  isTextArea: false,
  autoFocus: false,
  textareaLineHeight: 18,
  maxRows: 10,
  minRows: 1,
  showErrorAlways: false,
  placeholder: '',
  paddingLeft: undefined,
  withUnderline: true,
  disabled: false,
};

function InputWithContainer({ className, ...props }) {
  return (
    <Container className={className}>
      <Input {...props} />
    </Container>
  );
}

InputWithContainer.propTypes = {
  className: PropTypes.string,
};

// eslint-disable-next-line react/display-name
export default React.forwardRef((props, ref) => (
  <InputWithContainer inputRef={ref} {...props} />
));
